import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import "./style.css";
import Experience from "./Experience";
import { useProgress } from "@react-three/drei";

// -------------------- Loader with Video --------------------
function Loader({ onFinish }) {
  const { progress } = useProgress();
  const [displayProgress, setDisplayProgress] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [visible, setVisible] = useState(true);
  const videoRef = useRef(null);

  // Smooth progress increment
  useEffect(() => {
    if (progress > displayProgress) {
      const interval = setInterval(() => {
        setDisplayProgress((prev) => {
          if (prev < progress) return prev + 1;
          clearInterval(interval);
          return prev;
        });
      }, 20);
      return () => clearInterval(interval);
    }
  }, [progress, displayProgress]);

  // When progress hits 100, switch to video
  useEffect(() => {
    if (displayProgress >= 100) {
      const timeout = setTimeout(() => setShowVideo(true), 500);
      return () => clearTimeout(timeout);
    }
  }, [displayProgress]);

  // Unified finish handler (for skip + video end)
  const handleFinish = () => {
    setVisible(false);
    onFinish?.();
  };

  // Handle video ending
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showVideo) return;
    
    const handleVideoEnd = () => {
      handleFinish();
    };
    
    video.addEventListener("ended", handleVideoEnd);
    return () => video.removeEventListener("ended", handleVideoEnd);
  }, [showVideo]); // Added showVideo dependency

  if (!visible) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 overflow-hidden bg-black">
      {!showVideo ? (
        // -------------------- Progress Loader --------------------
        <div className="absolute inset-0 bg-black flex flex-col items-center justify-center">
          <div className="text-white text-2xl font-bold tracking-wider">
            {displayProgress}%
          </div>
          <div className="text-white text-lg mt-2 absolute bottom-0 mb-8">
            Loading...
          </div>
        </div>
      ) : (
        // -------------------- Video Screen --------------------
        <>
          <video
            ref={videoRef}
            src="/video.mp4"
            autoPlay
            preload="auto"
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
          <button
            onClick={handleFinish}
            className="absolute bottom-6 left-6 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg hover:bg-opacity-80 transition z-10"
          >
            Skip
          </button>
        </>
      )}
    </div>
  );
}

// -------------------- App Root --------------------
function App() {
  const [loadingComplete, setLoadingComplete] = useState(false);

  return (
    <>
      <div className="w-full h-full">
        <Experience showOverlay={loadingComplete} />
      </div>
      {!loadingComplete && (
        <Loader onFinish={() => setLoadingComplete(true)} />
      )}
    </>
  );
}

const root = ReactDOM.createRoot(document.querySelector("#root"));
root.render(<App />);